/**
 * Plan-limit policy predicates.
 *
 * Each limit is enforced at more than one moment — a UI pre-check that keeps a
 * dialog from opening, and the store action that actually mutates — so the rule
 * itself lives here once rather than being restated at each site, where the two
 * copies could drift apart and let the UI offer something the store refuses.
 */
import { UserModel } from '../types';
import { isAdmin } from '../utils/ostUtils';
import { resolveEffectiveTier } from './userCategories';
import { isTierAtLeast, FREE_PROJECT_LIMIT, FREE_NODE_LIMIT, INSIGHT_LIMIT } from './pricingTiers';

/**
 * Whether `user` may create another project given how many they already own.
 *
 * Admins are unrestricted, as are users whose effective tier is Pro or above —
 * `resolveEffectiveTier` is what folds power_user / zeytune_team categories into
 * a tier, so category-based elevation is handled without enumerating categories
 * here.
 */
export function canCreateProject(user: UserModel | null | undefined, projectCount: number): boolean {
    if (!user) return false;
    if (isAdmin(user)) return true;
    if (isTierAtLeast(resolveEffectiveTier(user), 'pro')) return true;
    return projectCount < FREE_PROJECT_LIMIT;
}

/**
 * Whether `user` may add another insight to the current project.
 *
 * Note this is an admin-only bypass, unlike the project cap: the insight limit
 * currently applies to paid tiers too. Kept as its own predicate so that stays
 * an explicit product decision rather than an accident of shared code.
 */
export function canAddInsight(user: UserModel | null | undefined, insightCount: number): boolean {
    if (isAdmin(user ?? null)) return true;
    return insightCount < INSIGHT_LIMIT;
}

/**
 * Whether `user` may add `countToAdd` more nodes to a tree that currently has
 * `currentNodeCount` nodes.
 *
 * Single choke point for the node cap: both the toolbar's add-node flow and
 * the slice panel's batch node creation (AI-suggested and manual slices alike)
 * funnel through this, so a path that forgets to check can't silently bypass
 * the cap the way the slice flow previously did. Mirrors `canCreateProject`'s
 * exemption model — admins and Pro-or-above accounts are unrestricted here;
 * the server enforces its own (higher) cap for paid tiers regardless.
 */
export function canAddNodes(
    user: UserModel | null | undefined,
    currentNodeCount: number,
    countToAdd: number,
): boolean {
    if (!user) return false;
    if (isAdmin(user)) return true;
    if (isTierAtLeast(resolveEffectiveTier(user), 'pro')) return true;
    return currentNodeCount + countToAdd <= FREE_NODE_LIMIT;
}
